import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import dayjs from "../../../core/config/dayjs";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  readPersistedLogs,
  writePersistedLogs,
} from "../../../core/cache/logsCacheStorage";
import { getLogs } from "../services/logsService";
import {
  dedupeLogs,
  extractJobStatuses,
  mapApiLog,
  sortLogsByCreatedTimeDesc,
} from "../services/logMappers";

export const getInitialDateRange = () => {
  const initialStartDate = dayjs()
    .tz("America/Los_Angeles")
    .subtract(30, "day");
  return {
    startDate: initialStartDate.toDate().toISOString(),
    endDate: new Date().toISOString(),
  };
};

const initialDateRange = getInitialDateRange();

// Full log fetches span dozens of pages and feed the Dashboard, Logs page,
// and Billing usage tab, so refetches are skipped while the store already
// holds fresh data for the same client/filter.
const LOGS_CACHE_TTL_MS = 5 * 60 * 1000;
// Persisted entries older than the TTL are still shown instantly while a
// background refresh runs; entries older than this are ignored outright.
const LOGS_PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const LOGS_MAX_PAGES = 100;
// Matches the browser's per-host connection limit; higher values only inflate
// request queueing against this API.
const LOGS_FETCH_CONCURRENCY = 6;

const normalizeFetchLogsPayload = (payload, state) => {
  const isObjectPayload = typeof payload === "object" && payload !== null;
  return {
    clientId: isObjectPayload
      ? Number(payload?.clientId) || getRequiredCurrentUserClientId(state)
      : Number(payload) || getRequiredCurrentUserClientId(state),
    deliveryStatus: isObjectPayload ? payload?.deliveryStatus || "" : "",
    pageSize:
      isObjectPayload && Number.isFinite(Number(payload?.limit))
        ? Number(payload.limit)
        : 100,
    forceRefresh: isObjectPayload ? Boolean(payload?.forceRefresh) : false,
  };
};

const buildLogsFetchKey = ({ clientId, deliveryStatus }) =>
  `${clientId}|${deliveryStatus}`;

// Reducer-safe variant: derives the key from the dispatch arg alone (no store
// access), returning null when the client id is not part of the arg.
const buildLogsFetchKeyFromArg = (payload) => {
  const isObjectPayload = typeof payload === "object" && payload !== null;
  const clientId = isObjectPayload
    ? Number(payload?.clientId) || 0
    : Number(payload) || 0;
  if (!clientId) return null;
  const deliveryStatus = isObjectPayload ? payload?.deliveryStatus || "" : "";
  return buildLogsFetchKey({ clientId, deliveryStatus });
};

export const fetchLogsThunk = createAsyncThunk(
  "logs/fetchLogs",
  async (payload = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const { clientId, deliveryStatus, pageSize, forceRefresh } =
        normalizeFetchLogsPayload(payload, getState());
      const fetchKey = buildLogsFetchKey({ clientId, deliveryStatus });

      if (!forceRefresh) {
        // The Redux store is empty after a page refresh; the persisted copy
        // renders instantly and only stale data triggers a network refresh.
        const persisted = await readPersistedLogs(fetchKey);
        const persistedAge = Date.now() - Number(persisted?.savedAt || 0);
        if (
          persisted?.result?.logs &&
          persistedAge <= LOGS_PERSIST_MAX_AGE_MS
        ) {
          if (persistedAge <= LOGS_CACHE_TTL_MS) {
            return persisted.result;
          }
          dispatch(logsHydratedFromCache(persisted.result));
        }
      }

      const firstResponse = await getLogs(clientId, {
        limit: pageSize,
        offset: 0,
        deliveryStatus,
      });

      if (firstResponse?.error) {
        return rejectWithValue(
          firstResponse.error.message || "Unable to load logs.",
        );
      }

      const firstBatch = Array.isArray(firstResponse?.logs)
        ? firstResponse.logs
        : [];
      const total = Number.isFinite(Number(firstResponse?.total))
        ? Number(firstResponse.total)
        : null;
      const batches = [...firstBatch];

      if (
        Number.isFinite(total) &&
        total > firstBatch.length &&
        firstBatch.length > 0
      ) {
        // The API caps limit at 100 but reports the total, so the remaining
        // pages can be fetched concurrently instead of one request at a time.
        const remainingOffsets = [];
        for (
          let offset = pageSize;
          offset < total && remainingOffsets.length < LOGS_MAX_PAGES;
          offset += pageSize
        ) {
          remainingOffsets.push(offset);
        }

        for (
          let index = 0;
          index < remainingOffsets.length;
          index += LOGS_FETCH_CONCURRENCY
        ) {
          const chunk = remainingOffsets.slice(
            index,
            index + LOGS_FETCH_CONCURRENCY,
          );
          const responses = await Promise.all(
            chunk.map((offset) =>
              getLogs(clientId, { limit: pageSize, offset, deliveryStatus }),
            ),
          );

          for (const response of responses) {
            if (response?.error) {
              return rejectWithValue(
                response.error.message || "Unable to load logs.",
              );
            }
            batches.push(...(Array.isArray(response?.logs) ? response.logs : []));
          }
        }
      } else if (total === null && firstBatch.length === pageSize) {
        // Total is unknown; fall back to sequential paging until a short page.
        let offset = firstBatch.length;
        let pageCount = 1;

        while (pageCount < LOGS_MAX_PAGES) {
          const response = await getLogs(clientId, {
            limit: pageSize,
            offset,
            deliveryStatus,
          });

          if (response?.error) {
            return rejectWithValue(
              response.error.message || "Unable to load logs.",
            );
          }

          const logsBatch = Array.isArray(response?.logs) ? response.logs : [];
          batches.push(...logsBatch);
          offset += logsBatch.length;
          pageCount += 1;

          if (logsBatch.length < pageSize) {
            break;
          }
        }
      }
      const sortedLogs = sortLogsByCreatedTimeDesc(dedupeLogs(batches).map(mapApiLog));

      const result = {
        logs: sortedLogs,
        jobStatusOptions: [
          { name: "Job Status", value: "" },
          ...extractJobStatuses(sortedLogs),
        ],
        fetchKey,
      };

      writePersistedLogs(fetchKey, { savedAt: Date.now(), result });

      return result;
    } catch {
      return rejectWithValue("Unable to load logs.");
    }
  },
  {
    condition: (payload = {}, { getState }) => {
      const state = getState();
      const normalized = normalizeFetchLogsPayload(payload, state);
      if (normalized.forceRefresh) {
        return true;
      }

      const fetchKey = buildLogsFetchKey(normalized);
      const logsState = state.logs;

      // An identical fetch is already in flight; let it finish instead of
      // firing a duplicate multi-page request storm.
      if (logsState.isLoading && logsState.pendingFetchKey === fetchKey) {
        return false;
      }

      const isFresh =
        logsState.lastFetchKey === fetchKey &&
        Date.now() - logsState.lastFetchedAt <= LOGS_CACHE_TTL_MS;
      return !isFresh;
    },
  },
);

const initialState = {
  logs: [],
  filteredLogs: [],
  isLoading: false,
  currentPage: 0,
  startDate: initialDateRange.startDate,
  endDate: initialDateRange.endDate,
  jobStatusOptions: [{ name: "Job Status", value: "" }],
  deliveryStatusFilter: "",
  searchQuery: "",
  dateRange: "all",
  spreadTable: false,
  showFilterModal: false,
  error: null,
  hasLoaded: false,
  pendingFetchKey: null,
  lastFetchKey: null,
  lastFetchedAt: 0,
};

const logsSlice = createSlice({
  name: "logs",
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setStartDate: (state, action) => {
      state.startDate = action.payload
        ? new Date(action.payload).toISOString()
        : null;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload
        ? new Date(action.payload).toISOString()
        : null;
    },
    setDeliveryStatusFilter: (state, action) => {
      state.deliveryStatusFilter = action.payload;
      state.currentPage = 0;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.currentPage = 0;
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
      state.currentPage = 0;
    },
    setSpreadTable: (state, action) => {
      state.spreadTable = Boolean(action.payload);
    },
    setShowFilterModal: (state, action) => {
      state.showFilterModal = action.payload;
    },
    logsHydratedFromCache: (state, action) => {
      // Paints persisted logs immediately; isLoading stays true because a
      // background refresh is still running.
      state.logs = action.payload.logs || [];
      state.filteredLogs = action.payload.logs || [];
      state.jobStatusOptions = action.payload.jobStatusOptions || [
        { name: "Job Status", value: "" },
      ];
      state.hasLoaded = true;
    },
    resetLogFilters: (state) => {
      const nextRange = getInitialDateRange();
      state.startDate = nextRange.startDate;
      state.endDate = nextRange.endDate;
      state.deliveryStatusFilter = "";
      state.searchQuery = "";
      state.dateRange = "all";
      state.currentPage = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLogsThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.pendingFetchKey = buildLogsFetchKeyFromArg(action.meta.arg);
      })
      .addCase(fetchLogsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.logs = action.payload.logs;
        state.filteredLogs = action.payload.logs;
        state.jobStatusOptions = action.payload.jobStatusOptions || [
          { name: "Job Status", value: "" },
        ];
        state.hasLoaded = true;
        state.pendingFetchKey = null;
        state.lastFetchKey = action.payload.fetchKey;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchLogsThunk.rejected, (state, action) => {
        state.isLoading = false;
        // Keep any cache-hydrated logs on screen; only reflect the failure
        // when there is nothing to show.
        if (!state.logs.length) {
          state.error = action.payload || "Unable to load logs.";
        }
        state.hasLoaded = true;
        state.pendingFetchKey = null;
        state.lastFetchKey = null;
        state.lastFetchedAt = 0;
      });
  },
});

export const {
  logsHydratedFromCache,
  resetLogFilters,
  setCurrentPage,
  setDateRange,
  setDeliveryStatusFilter,
  setEndDate,
  setSearchQuery,
  setShowFilterModal,
  setSpreadTable,
  setStartDate,
} = logsSlice.actions;

export const selectLogs = (state) => state.logs.logs;
export const selectFilteredLogs = (state) => state.logs.filteredLogs;
export const selectLogsLoading = (state) => state.logs.isLoading;
export const selectLogsCurrentPage = (state) => state.logs.currentPage;
export const selectLogsStartDate = (state) => state.logs.startDate;
export const selectLogsEndDate = (state) => state.logs.endDate;
export const selectJobStatusOptions = (state) => state.logs.jobStatusOptions;
export const selectDeliveryStatusFilter = (state) => state.logs.deliveryStatusFilter;
export const selectLogsSearchQuery = (state) => state.logs.searchQuery;
export const selectLogsDateRange = (state) => state.logs.dateRange;
export const selectLogsSpreadTable = (state) => state.logs.spreadTable;
export const selectLogsShowFilterModal = (state) => state.logs.showFilterModal;
export const selectLogsError = (state) => state.logs.error;
export const selectLogsHasLoaded = (state) => state.logs.hasLoaded;

export const logsInitialState = initialState;

export default logsSlice.reducer;
